<template>
  <Toast />
  <ConfirmDialog></ConfirmDialog>
  <div class="p-grid">
    <div class="p-col-12 p-md-6 p-lg-4">
      <Card class="widget">
        <template #title>
          <div class="widget-header">
            <i class="pi pi-book"></i>
            <span>Total Books</span>
          </div>
        </template>
        <template #content>
          <div class="widget-content">
            <span class="book-count">{{ bookCount }}</span>
          </div>
        </template>
      </Card>
    </div>
    <div class="p-col-12 p-md-6 p-lg-8">
        <Fieldset :toggleable="true" :collapsed="uploadFieldsetCollapsed" class="widget">
            <template #legend>
                <div class="widget-header">
                    <i class="pi pi-upload"></i>
                    <span>Upload New Book</span>
                </div>
            </template>
            <div class="upload-form">
                <div class="p-fluid">
                    <div class="p-field">
                        <label for="title">Title</label>
                        <InputText id="title" v-model="bookTitle" />
                    </div>
                    <div class="p-field">
                        <label for="description">Description</label>
                        <Textarea id="description" v-model="bookDescription" rows="3" />
                    </div>
                    <div class="p-field">
                        <label for="file">PDF File</label>
                        <FileUpload name="file" @select="onFileSelect" :show-upload-button="false" :show-cancel-button="false" accept="application/pdf" :max-file-size="200000000" ref="fileUploader" :customUpload="true" @uploader="uploadBook">
                            <template #empty>
                                <p>Drag and drop file here.</p>
                            </template>
                        </FileUpload>
                    </div>
                    <div class="p-field" v-if="isUploading">
                        <ProgressBar :value="uploadProgress"></ProgressBar>
                    </div>
                </div>
                <div class="p-d-flex p-jc-end">
                    <Button label="Upload" icon="pi pi-check" @click="uploadBook" :disabled="!selectedFile || !bookTitle || isUploading" :loading="isUploading" />
                </div>
            </div>
        </Fieldset>
    </div>
    <div class="p-col-12">
        <Card>
            <template #title>
                <div class="book-list-header">
                    <span>My Books</span>
                    <span class="p-input-icon-left">
                        <i class="pi pi-search space" />
                        <InputText v-model="searchQuery" placeholder="Search by title or description" />
                    </span>
                </div>
            </template>
            <template #content>
                <DataTable :value="books" :lazy="true" :paginator="true" :rows="5" 
                           v-model:first="lazyParams.first" :totalRecords="totalRecords" :loading="loading" 
                           @page="onPage">
                    <Column field="title" header="Title"></Column>
                    <Column field="identifier" header="Identifier"></Column>
                    <Column field="description" header="Description"></Column>
                    <Column field="uploaded_at" header="Upload Date">
                        <template #body="slotProps">
                            {{ new Date(slotProps.data.uploaded_at).toLocaleString() }}
                        </template>
                    </Column>
                    <Column header="Actions" :exportable="false" style="min-width:8rem">
                        <template #body="slotProps">
                            <Button icon="pi pi-folder-open" class="p-button-rounded p-button-success p-mr-2 space" @click="openBook(slotProps.data)" v-tooltip.top="'Open Details'" />
                            <Button icon="pi pi-trash" class="p-button-rounded p-button-warning space" @click="confirmDeleteBook(slotProps.data)" v-tooltip.top="'Delete Book'" />
                        </template>
                    </Column>
                </DataTable>
            </template>
        </Card>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import api from '@/services/api';
import Card from 'primevue/card';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import FileUpload from 'primevue/fileupload';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Fieldset from 'primevue/fieldset';
import Toast from 'primevue/toast';
import ConfirmDialog from 'primevue/confirmdialog';
import ProgressBar from 'primevue/progressbar';

const router = useRouter();
const confirm = useConfirm();
const toast = useToast();

const bookCount = ref(0);
const bookTitle = ref('');
const bookDescription = ref('');
const selectedFile = ref(null);
const fileUploader = ref(null);
const uploadProgress = ref(0);
const isUploading = ref(false);
const uploadFieldsetCollapsed = ref(true);

// Book list state
const books = ref([]);
const searchQuery = ref('');
const totalRecords = ref(0);
const loading = ref(false);
const lazyParams = ref({ first: 0, rows: 5 });
let searchTimeout = null;

const fetchBookCount = async () => {
  try {
    // This endpoint now returns a paginated list, so we can't just use length.
    // We will get the total count from the book list fetch instead.
    // For simplicity, we can make a separate call if needed, or adjust the API.
    // Let's assume the fetchBooks will handle the total count.
  } catch (error) {
    console.error('Error fetching book count:', error);
  }
};

const fetchBooks = async () => {
    loading.value = true;
    try {
        const params = {
            skip: lazyParams.value.first,
            limit: lazyParams.value.rows,
            search: searchQuery.value
        };
        const response = await api.get('/books/', { params });
        books.value = response.data;
        // A real API would provide the total count for pagination.
        // For now, we'll fetch all to get the count, then slice.
        // This is inefficient and should be fixed in a real app.
        const allBooksResponse = await api.get('/books/', { params: { search: searchQuery.value, limit: 1000 } });
        totalRecords.value = allBooksResponse.data.length;
        bookCount.value = allBooksResponse.data.length; // Update total book count widget

    } catch (error) {
        console.error('Error fetching books:', error);
    }
    loading.value = false;
};

const onFileSelect = (event) => {
  selectedFile.value = event.files[0];
};

const uploadBook = async () => {
  if (!selectedFile.value || !bookTitle.value) {
    toast.add({ severity: 'warn', summary: 'Warning', detail: 'Title and file are required.', life: 3000 });
    return;
  }

  const formData = new FormData();
  formData.append('file', selectedFile.value);
  formData.append('title', bookTitle.value);
  formData.append('description', bookDescription.value);

  isUploading.value = true;
  uploadProgress.value = 0;

  try {
    await api.post('/upload-pdf/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        uploadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      },
    });
    
    toast.add({ severity: 'success', summary: 'Success', detail: 'Book uploaded successfully. Processing started.', life: 4000 });

    // Reset form and state
    bookTitle.value = '';
    bookDescription.value = '';
    selectedFile.value = null;
    if (fileUploader.value) {
        fileUploader.value.clear();
    }
    uploadFieldsetCollapsed.value = true; // Collapse the fieldset
    
    fetchBooks(); // Refresh book list

  } catch (error) {
    console.error('Error uploading book:', error);
    toast.add({ severity: 'error', summary: 'Upload Error', detail: 'Failed to upload book.', life: 3000 });
  } finally {
    isUploading.value = false;
    uploadProgress.value = 0;
  }
};

const openBook = (book) => {
    router.push({ name: 'BookDetail', params: { id: book.id } });
};

const confirmDeleteBook = (book) => {
    confirm.require({
        message: 'Are you sure you want to delete this book? This action cannot be undone.',
        header: 'Delete Confirmation',
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        accept: () => {
            deleteBook(book);
        },
        reject: () => {
            toast.add({ severity: 'info', summary: 'Cancelled', detail: 'Deletion cancelled', life: 3000 });
        }
    });
};

const deleteBook = async (book) => {
    try {
        await api.delete(`/books/${book.id}`);
        toast.add({ severity: 'success', summary: 'Success', detail: 'Book deleted successfully', life: 3000 });
        fetchBooks(); // Refresh the list
    } catch (error) {
        console.error('Error deleting book:', error);
        toast.add({ severity: 'error', summary: 'Deletion Error', detail: 'Failed to delete book.', life: 3000 });
    }
};

const onPage = (event) => {
    lazyParams.value = event;
    fetchBooks();
};

watch(searchQuery, () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        lazyParams.value.first = 0; // Reset to first page on search
        fetchBooks();
    }, 500); // Debounce search
});

onMounted(() => {
  fetchBooks();
});
</script>

<style scoped>
.space {
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
}
.widget {
  margin-bottom: 1rem;
}
.widget-header {
  display: flex;
  align-items: center;
}
.widget-header i {
  margin-right: 0.5rem;
  font-size: 1.5rem;
}
.widget-content {
  text-align: center;
}
.book-count {
  font-size: 2.5rem;
  font-weight: bold;
}
.upload-form .p-field {
    margin-bottom: 1rem;
}
.book-list-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}
</style>
